import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { UploadCloudIcon, FileSpreadsheetIcon, XIcon, RefreshCwIcon, ArrowRightIcon, CalendarClockIcon, RowsIcon, Columns3Icon, AlertCircleIcon, Loader2Icon } from 'lucide-react';
import type { FileMeta, UploadAnalysis, ValidationSummary } from '../../lib/types';
import { parseCsvUpload } from '../../lib/csvUpload';
import { StatusBadge } from '../ui/StatusBadge';
import { formatNumber, cn } from '../../lib/utils';
interface Props {
  file: FileMeta | null;
  summary: ValidationSummary | null;
  onFileSelected: (analysis: UploadAnalysis) => void;
  onRemove: () => void;
  onContinue: () => void;
}
export function Step1Upload({
  file,
  summary,
  onFileSelected,
  onRemove,
  onContinue
}: Props) {
  const [dragging, setDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  async function readFile(selectedFile: File | undefined) {
    if (!selectedFile) return;
    setError(null);
    setIsParsing(true);
    try {
      onFileSelected(await parseCsvUpload(selectedFile));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not read this file. Please try another CSV.');
    } finally {
      setIsParsing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }
  function clearFile() {
    onRemove();
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }
  function browseForReplacement() {
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.click();
  }
  return <div className="mx-auto max-w-3xl" data-id="element-29" data-mp="hnnjptg4xax7">
      {!file ? <div onDragOver={event => {
      event.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={event => {
      event.preventDefault();
      setDragging(false);
      void readFile(event.dataTransfer.files[0]);
    }} className={cn('flex flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-white px-6 py-16 text-center transition-colors', dragging ? 'border-salesforce-500 bg-salesforce-50' : 'border-gray-300')} data-id="element-30" data-mp="hm44m667t5x0">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-salesforce-50" data-id="element-31" data-mp="162rz8n24i2lu">
            {isParsing ? <Loader2Icon className="h-8 w-8 animate-spin text-salesforce-600" data-id="element-32" data-mp="waw0d9s7q8gk" /> : <UploadCloudIcon className="h-8 w-8 text-salesforce-600" data-id="element-33" data-mp="w8j8a3sqqhps" />}
          </div>
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-34" data-mp="rbsu5m0hxw70">{isParsing ? 'Reading your CSV…' : 'Drag & drop your CSV file here'}</h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-35" data-mp="iauxxpwausyz">or click below to browse your computer</p>
          <button type="button" disabled={isParsing} onClick={browseForReplacement} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-salesforce-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-salesforce-600 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-36" data-mp="dbru28s22ssl">
            <FileSpreadsheetIcon className="h-4 w-4" data-id="element-37" data-mp="2yxkjah6yrllc" />
            Browse files
          </button>
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={event => void readFile(event.target.files?.[0])} data-id="element-38" data-mp="32yml95s9mbr0" />
          <p className="mt-6 text-xs text-gray-400" data-id="element-39" data-mp="26a6ux7hbzycu">Accepted format: <span className="font-medium text-gray-500" data-id="element-40" data-mp="2b7gq73rej48l">.csv</span> · Max size 25 MB · UTF-8 encoded</p>
          {error && <div role="alert" className="mt-5 flex max-w-lg items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-left text-sm text-red-700" data-id="element-41" data-mp="2f890m0c0ff6g">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-42" data-mp="1nidzz7ipc32h" />
              <span data-id="element-43" data-mp="1ng1kr24bw6t4">{error}</span>
            </div>}
        </div> : <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-5" data-id="element-44" data-mp="1rhwmu8u32o7y">
          <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-card" data-id="element-45" data-mp="18fe2ur1kpycn">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50" data-id="element-46" data-mp="13e6q0dkkq8z4"><FileSpreadsheetIcon className="h-6 w-6 text-emerald-600" data-id="element-47" data-mp="1i70jj2wokpms" /></div>
            <div className="min-w-0 flex-1" data-id="element-48" data-mp="1dcmlstujq6ey"><p className="truncate text-sm font-semibold text-gray-900" data-id="element-49" data-mp="1dccv141ofv87">{file.name}</p><p className="text-xs text-gray-500" data-id="element-50" data-mp="1r3rch6fxrod5">{file.sizeLabel} · {formatNumber(file.rows)} rows detected</p></div>
            <div className="flex items-center gap-2" data-id="element-51" data-mp="1m8vii0r52qxe">
              <button type="button" onClick={browseForReplacement} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50" data-id="element-52" data-mp="212qn2uk9hu2s"><RefreshCwIcon className="h-3.5 w-3.5" data-id="element-53" data-mp="6qk2541kfyxa" />Replace</button>
              <button type="button" onClick={clearFile} aria-label="Remove file" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600" data-id="element-54" data-mp="1rfr07v2zr4b"><XIcon className="h-4 w-4" data-id="element-55" data-mp="2icb3wkbnuk3h" /></button>
            </div>
          </div>
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={event => void readFile(event.target.files?.[0])} data-id="element-56" data-mp="23nol28mtra7i" />
          {error && <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-id="element-57" data-mp="1un069bplx5tk"><AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-58" data-mp="1ypwkkq846frp" /><span data-id="element-59" data-mp="1kpc2xxdxqpzs">{error}</span></div>}

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-card" data-id="element-60" data-mp="1kqdnqtkr6qup">
            <div className="mb-4 flex items-center justify-between" data-id="element-61" data-mp="1pnnce70biyhh">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-62" data-mp="3f3d6uwoce36q">Upload summary</h3>
              <StatusBadge status={summary?.errorRows ? 'error' : summary?.warningRows ? 'warning' : 'valid'} labelOverride={validationLabel(summary)} size="sm" data-id="element-63" data-mp="3f1k2xp545x2f" />
            </div>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-64" data-mp="3imlvyon2iwgt">
              <SummaryStat icon={RowsIcon} label="Total rows" value={formatNumber(file.rows)} data-id="element-65" data-mp="3dl66mkduy3sm" />
              <SummaryStat icon={Columns3Icon} label="Total columns" value={String(file.columns)} data-id="element-66" data-mp="38o61cbou152k" />
              <SummaryStat icon={CalendarClockIcon} label="Uploaded" value={file.uploadedAt} small data-id="element-67" data-mp="33p38dpm4mpay" />
              <SummaryStat icon={FileSpreadsheetIcon} label="File size" value={file.sizeLabel} data-id="element-68" data-mp="55csudawv6f9" />
            </dl>
          </div>
          <div className="flex justify-end" data-id="element-69" data-mp="834l0z9d0t0"><button type="button" onClick={onContinue} className="inline-flex items-center gap-2 rounded-lg bg-salesforce-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-salesforce-600" data-id="element-70" data-mp="3rlz49dzghezy">Continue to Mapping<ArrowRightIcon className="h-4 w-4" data-id="element-71" data-mp="nx5nsd9jj1pm" /></button></div>
        </motion.div>}
    </div>;
}
function validationLabel(summary: ValidationSummary | null): string {
  if (!summary) return 'Validated';
  if (summary.errorRows > 0) return `${summary.errorRows} error${summary.errorRows === 1 ? '' : 's'} · ${summary.warningRows} warning${summary.warningRows === 1 ? '' : 's'}`;
  if (summary.warningRows > 0) return `Validated · ${summary.warningRows} warning${summary.warningRows === 1 ? '' : 's'}`;
  return 'Validated · no issues found';
}
function SummaryStat({
  icon: Icon,
  label,
  value,
  small
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  small?: boolean;
}) {
  return <div className="rounded-xl bg-gray-50 p-3" data-id="element-72" data-mp="1eer7hvkp2gbu"><div className="mb-1 flex items-center gap-1.5 text-gray-400" data-id="element-73" data-mp="1edg25rblp0im"><Icon className="h-3.5 w-3.5" data-id="element-74" data-mp="1j5pokxdibig5" /><dt className="text-[11px] font-medium uppercase tracking-wide" data-id="element-75" data-mp="1j6iww2s46g0e">{label}</dt></div><dd className={cn('font-semibold text-gray-900', small ? 'text-xs' : 'text-base')} data-id="element-76" data-mp="1o40zzcjga10h">{value}</dd></div>;
}